import { useState } from 'react';
import {
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import type {
  AdminSettableSubscriptionTier,
  PortalUser,
} from '../types/users.types';
import { QuotaOverrideEditor } from './quota-override-editor';
import { SubscriptionTierEditor } from './subscription-tier-editor';

interface UserManagementPanelProps {
  user: PortalUser;
  globalCreativeQuota?: number;
  disabled?: boolean;
  onBlock: () => void;
  onUnblock: () => void;
  onSetBillingConversionDisabled: (value: boolean) => void;
  onSetConversionModalHidden: (value: boolean) => void;
  onSaveQuotaOverride: (value: number | null) => Promise<{ ok: boolean; message?: string }>;
  onSaveSubscriptionTier: (
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => Promise<{ ok: boolean; message?: string }>;
}

export function UserManagementPanel({
  user,
  globalCreativeQuota,
  disabled = false,
  onBlock,
  onUnblock,
  onSetBillingConversionDisabled,
  onSetConversionModalHidden,
  onSaveQuotaOverride,
  onSaveSubscriptionTier,
}: UserManagementPanelProps) {
  const [billingConfirmOpen, setBillingConfirmOpen] = useState(false);
  const [pendingBillingValue, setPendingBillingValue] = useState<boolean | null>(null);

  const billingConfirmMessage = pendingBillingValue
    ? 'This exempts the user from all billing limits. Continue?'
    : 'This removes the billing exemption and restores tiered billing limits. Continue?';

  const handleBillingConfirm = () => {
    if (pendingBillingValue === null) {
      return;
    }

    onSetBillingConversionDisabled(pendingBillingValue);
    setBillingConfirmOpen(false);
    setPendingBillingValue(null);
  };

  const effectiveGlobalQuota = globalCreativeQuota ?? user.monthly_limit;

  return (
    <div className="space-y-8">
      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Subscription tier
        </h3>
        <SubscriptionTierEditor
          workspaceId={user.workspace_id}
          tier={user.subscription}
          disabled={disabled}
          onSave={onSaveSubscriptionTier}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Creative quota override
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Overrides the global monthly creative limit for this user only.
        </p>
        <QuotaOverrideEditor
          value={user.creative_quota_override}
          globalDefault={effectiveGlobalQuota}
          disabled={disabled}
          onSave={onSaveQuotaOverride}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Account actions
        </h3>
        <div className="flex flex-wrap gap-2">
          {user.status === 'blocked' ? (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={onUnblock}
            >
              Unblock user
            </PortalDsButton>
          ) : (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={onBlock}
            >
              Block user
            </PortalDsButton>
          )}
        </div>
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Billing
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Exemption removes limits; hiding prompts only changes paywall messaging.
        </p>
        <div className="flex flex-wrap gap-2">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setPendingBillingValue(!user.disable_billing_conversion);
              setBillingConfirmOpen(true);
            }}
          >
            {user.disable_billing_conversion
              ? 'Remove billing exemption'
              : 'Exempt from billing limits'}
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => onSetConversionModalHidden(!user.hide_conversion_modal)}
          >
            {user.hide_conversion_modal
              ? 'Show upgrade prompts'
              : 'Hide upgrade prompts'}
          </PortalDsButton>
        </div>
      </section>

      <Dialog
        open={billingConfirmOpen}
        onOpenChange={(nextOpen) => {
          setBillingConfirmOpen(nextOpen);
          if (!nextOpen) {
            setPendingBillingValue(null);
          }
        }}
      >
        <DialogContent className="max-w-md border-[#EDEEF2] bg-white p-0 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
          <DialogHeader className="border-b border-[#EDEEF2] px-5 py-4 text-left">
            <DialogTitle className="text-[15px] font-semibold text-[#0D0F1A]">
              Confirm billing exemption change
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#5E6278]">
              {billingConfirmMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 px-5 py-4 sm:justify-end">
            <PortalDsButton
              variant="secondary"
              size="sm"
              onClick={() => {
                setBillingConfirmOpen(false);
                setPendingBillingValue(null);
              }}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="primary"
              size="sm"
              onClick={handleBillingConfirm}
            >
              Continue
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
